import React from "react";
import date from "./dateAdapter.js";
import VolunteerHours from "./hours.jsx";
import * as search from "./search.jsx";
import * as utils from "./utils.jsx";

import { useBackendData, useFormState } from "./hooks.ts";

var FormField = utils.FormField;
var DateRange = utils.DateRange;

var totalHours = function (report) {
  return (
    +report.site_hours +
    +report.sitePrepHours +
    +report.adminHours +
    +report.travelHours * report.crew_count
  );
};

function WorkAccomplishedSummary({ report }) {
  return (
    <ul>
      {(report.treadBuilt && <li>{report.treadBuilt} ft Trail Built</li>) || ""}
      {(report.retread && <li>{report.retread} ft Retread</li>) || ""}
      {(report.milesCovered && <li>{report.milesCovered} Miles Covered</li>) ||
        ""}
      {(report.rehabRevegFt && <li>{report.rehabRevegFt} ft Rehab/Reveg</li>) ||
        ""}
      {(report.logoutMiles && <li>{report.logoutMiles} Logout Miles</li>) || ""}
      {(report.logsRemoved && (
        <li>{report.logsRemoved} Logs Removed (above 3")</li>
      )) ||
        ""}
      {(report.brushClearedMiles && (
        <li>{report.brushClearedMiles} Miles Brush Cleared</li>
      )) ||
        ""}
      {(report.drains && <li>{report.drains} Drains Cleaned or Created</li>) ||
        ""}
      {(report.structures && report.structures.length && (
        <li>
          Structures
          <ul>
            {report.structures.map((v) => (
              <li key={v.structureId}>
                {v.structureType}
                {v.reportedName ? ` "${v.reportedName}"` : ""}:{" "}
                {v.builtOrMaintained}{" "}
                {v.structureFeet ? v.structureFeet + " ft" : ""}
                {v.totalLength ? ` of total ${v.structureFeet} ft` : ""}
                {v.status ? " [" + v.status + "]" : ""}
              </li>
            ))}
          </ul>
        </li>
      )) ||
        ""}
    </ul>
  );
}

function Structure({ name, initialValue, errors, onChange, onRemove }) {
  const { value, handleChange } = useFormState({
    name,
    initialValue,
    onChange,
  });

  const error = errors ? errors[name] : null;
  return (
    <div>
      {error ? (
        <div>
          <br />
          <div className="module validation-error">{error}</div>
        </div>
      ) : null}
      <div className="flex">
        <FormField
          name={`${name}.reportedName`}
          type="text"
          label="Structure name"
          placeholder="(optional)"
          value={value.reportedName || ""}
          errors={errors}
          onChange={handleChange}
          flexBasis="10%"
        />
        <FormField
          name={`${name}.structureType`}
          type="select"
          label="Type of Structure"
          value={value.structureType || ""}
          errors={errors}
          onChange={handleChange}
          required
          flexBasis="15%"
        >
          <option value="">Please select one</option>
          <option value="Steps">Steps</option>
          <option value="Log cribbing">Log cribbing</option>
          <option value="Rock cribbing">Rock cribbing</option>
          <option value="Bridge">Bridge</option>
          <option value="Turnpike">Turnpike</option>
          <option value="Puncheon">Puncheon</option>
          <option value="Culvert">Culvert</option>
        </FormField>
        <FormField
          name={`${name}.builtOrMaintained`}
          label="Built, maintained or removed?"
          type="select"
          value={value.builtOrMaintained || ""}
          errors={errors}
          onChange={handleChange}
          required
          flexBasis="15%"
        >
          <option value="">Please select one</option>
          <option value="Built">Built</option>
          <option value="Maintained">Maintained</option>
          <option value="Removed">Removed</option>
        </FormField>
        <FormField
          name={`${name}.status`}
          label="Status"
          type="select"
          value={value.status || ""}
          errors={errors}
          onChange={handleChange}
          required
          flexBasis="15%"
        >
          <option value="">Please select one</option>
          <option value="In progress">In progress</option>
          <option value="Completed">Completed</option>
        </FormField>
        <FormField
          type="number"
          name={`${name}.structureFeet`}
          label="Progress completed (ft)"
          step="0.1"
          min="0"
          value={value.structureFeet || ""}
          errors={errors}
          onChange={handleChange}
          required
          flexBasis="10%"
        />
        {value.status === "Completed" ? (
          <FormField
            type="number"
            name={`${name}.totalLength`}
            label="Total length of structure (ft)"
            step="0.1"
            min="0"
            value={value.totalLength || ""}
            errors={errors}
            onChange={handleChange}
            required
            flexBasis="10%"
          />
        ) : (
          <div style={{ flexBasis: "10%" }}></div>
        )}
        <button
          type="button"
          className="red ui button"
          style={{ textAlign: "center", flexBasis: "10%" }}
          onClick={onRemove}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function Structures({
  name,
  initialValue,
  initialRemoved,
  errors,
  onChange,
  setRequestState,
}) {
  const { value, setValue } = useFormState({
    name,
    initialValue: initialValue?.length ? initialValue : [{}],
    onChange,
  });
  const [removed, setRemoved] = React.useState(initialRemoved || []);

  function handleAdd() {
    const newValue = [...value, {}];
    setValue(newValue);
    onChange(newValue, name);
  }

  function handleChange(i, changedValue) {
    const newValue = [...value];
    newValue[i] = changedValue;
    setValue(newValue);
    onChange(newValue, name);
  }

  function handleRemove(i) {
    const newRemoved = value[i].structureId
      ? [...removed, value[i].structureId]
      : removed;
    setRemoved(newRemoved);
    onChange(newRemoved, name + "_removed");
    const newValue = value.filter((_item, index) => index !== i);
    setValue(newValue);
    onChange(newValue, name);
    if (errors && errors[name + "." + i]) {
      const newErrors = { ...errors };
      delete newErrors[name + "." + i];
      setRequestState((current) => ({ ...current, errors: newErrors }));
    }
  }

  return (
    <div className="field">
      <br />
      <br />
      <h2>Structures Built, Maintained or Removed</h2>
      <hr />
      {value.map((item, key) => (
        <Structure
          key={item.structureId || key}
          name={`${name}.${key}`}
          initialValue={item}
          errors={errors}
          onChange={handleChange.bind(null, key)}
          onRemove={handleRemove.bind(null, key)}
        />
      ))}
      <br />
      <button type="button" className="green ui button" onClick={handleAdd}>
        Add a Structure
      </button>
    </div>
  );
}

function WPReportForm({ wp, action, initialValue, onSave }) {
  const {
    value,
    setValue,
    error,
    errors,
    submitting,
    handleChange,
    handleSubmit,
    setRequestState,
  } = useFormState({
    action,
    initialValue,
    validate,
    handleSubmitted,
  });

  function handleTrailHoursChange(newValue, key) {
    if (newValue.target) {
      var e = newValue;
      newValue =
        e.target.type == "checkbox" ? e.target.checked : e.target.value;
      var parts = e.target.name.split(".");
      key = parts[parts.length - 1];
    }
    var index = key.split("_")[1];
    const percent = parseInt(newValue);
    const hours = Math.round((percent / 100) * totalHours(value));
    const trail_hours = value.hours_by_trail.map((trail, trailIndex) =>
      trailIndex === Number(index)
        ? {
            ...trail,
            wp_segment_pct: percent,
            wp_segment_hours: hours,
          }
        : trail,
    );
    setValue({ ...value, hours_by_trail: trail_hours });
  }

  function handleAwardsDeliveredChange(newValue, key) {
    setValue({
      ...value,
      [key]: newValue,
      awards_value: newValue,
      awards_delivered: value.awards_delivered.map((award) => ({
        ...award,
        status: newValue.indexOf(award.value) >= 0 ? "Awarded" : "Owed",
      })),
    });
  }

  function handleAwardsFutureChange(newValue, key) {
    setValue({
      ...value,
      [key]: newValue,
      awards_future_value: newValue,
      awards_future: value.awards_future.map((award) => ({
        ...award,
        status: newValue.indexOf(award.value) >= 0 ? "Awarded" : "Owed",
      })),
    });
  }

  function validate() {
    var sum = 0;
    for (var i = 0; i < value.hours_by_trail.length; i++) {
      sum += value.hours_by_trail[i]["wp_segment_pct"];
    }
    if (value.hours_by_trail.length > 1 && sum != 100) {
      return { seg_0: "Total percentage must be 100%" };
    }
  }

  function handleSubmitted(data) {
    const newValue = { ...value, _id: data["_id"] };
    setValue(newValue);
    onSave(newValue);
  }

  function hoursUpdated(data) {
    var total_hours = 0;
    for (var key in data) {
      for (var date in data[key]) {
        if (typeof data[key][date] !== "object") continue;
        total_hours += +data[key][date]?.hours || 0;
      }
    }
    setValue({ ...value, site_hours: total_hours });
  }

  const report = value;
  var is_outreach = wp.work_party_type.name == "Outreach Event";

  report.messageToCrewTitle = report.messageToCrewTitle
    ? report.messageToCrewTitle
    : "Thank you for volunteering with WTA! " +
      wp.name +
      " – " +
      date.format(date.toDate(wp.start_date_time), "MMM D");

  return (
    <form>
      <div className="container">
        <h2>Time &amp; Location</h2>
        <hr />
        <div className="row">
          <div className="col three">
            <h3>Date</h3>
          </div>
          <div className="col seven">
            <p>
              <DateRange start={wp.start_date_time} end={wp.end_date_time} />
            </p>
          </div>
        </div>
        {is_outreach ? (
          ""
        ) : (
          <div>
            <hr />
            <div className="row">
              <div className="col three">
                <h3>Additional trails worked on (use ';' to separate)</h3>
              </div>
              <div className="col seven">
                <FormField
                  name="additionalTrails"
                  type="text"
                  errors={errors}
                  value={report.additionalTrails}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="row">
              <div className="col three">&nbsp;</div>
              <div className="col seven">
                {wp.wp_trail_segments &&
                  wp.wp_trail_segments.map((wp_seg) => (
                    <p
                      key={wp_seg.trail_segment.name}
                      className="ui tag disabled"
                    >
                      {wp_seg.trail_segment.name}
                    </p>
                  ))}
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col three">
                <h3>District</h3>
              </div>
              <div className="col seven">
                <p>{report.land_manager ? report.land_manager.name : ""}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <hr />
      <div className="container">
        <h2>Crew</h2>
        <hr />
        <div className="row">
          <div className="col three">
            <h3>Crew Leaders</h3>
          </div>
          <div className="col seven">
            <p>{wp.leader ? wp.leader.name : ""}</p>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col three">
            <h3>Participants</h3>
          </div>
          <div className="col seven">
            <p>
              {report.crew_count}{" "}
              {(is_outreach && "Ambassadors") || "Crew Members"} (
              {report.youth_count} Youth)
            </p>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col three">&nbsp;</div>
          <div className="col two">Hours</div>
          <div className="col one">&nbsp;</div>
          <div className="col two" style={{ textAlign: "center" }}>
            Participants
          </div>
          <div className="col one">&nbsp;</div>
          <div className="col two">Total</div>
        </div>

        {is_outreach ? (
          ""
        ) : (
          <div>
            <div className="row">
              <div className="col three">
                <h3>Site Preparation</h3>
              </div>
              <div className="col two">
                <FormField
                  name="sitePrepHours"
                  type="text"
                  value={report.sitePrepHours}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-cancel"></span>
                </h3>
              </div>
              <div className="col two">
                <h3 style={{ textAlign: "center" }}>1 CL</h3>
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-eq"></span>
                </h3>
              </div>
              <div className="col two">
                <h3>{report.sitePrepHours || 0} Hours</h3>
              </div>
            </div>
            <div className="row">
              <div className="col three">
                <h3>Administration</h3>
              </div>
              <div className="col two">
                <FormField
                  type="text"
                  name="adminHours"
                  value={report.adminHours}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-cancel"></span>
                </h3>
              </div>
              <div className="col two">
                <h3 style={{ textAlign: "center" }}>1 CL</h3>
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-eq"></span>
                </h3>
              </div>
              <div className="col two">
                <h3>{report.adminHours || 0} Hours</h3>
              </div>
            </div>
            <div className="row">
              <div className="col three">
                <h3>Travel Time</h3>
              </div>
              <div className="col two">
                <FormField
                  type="text"
                  name="travelHours"
                  value={report.travelHours}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-cancel"></span>
                </h3>
              </div>
              <div className="col two">
                <h3 style={{ textAlign: "center" }}>{report.crew_count}</h3>
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-eq"></span>
                </h3>
              </div>
              <div className="col two">
                <h3>{+report.travelHours * report.crew_count} Hours</h3>
              </div>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col three">
            <h3>On Site Time</h3>
          </div>
          <div className="col five">
            <h3>
              {report.site_hours.toFixed(2)}
              <utils.Modal
                Component={VolunteerHours}
                wp={wp}
                onSaved={hoursUpdated}
              >
                {" "}
                <button className="small green ui button">
                  Edit Volunteer Hours
                </button>
              </utils.Modal>
            </h3>
          </div>
          <div className="col one">
            <h3>
              <span className="icon-eq"></span>
            </h3>
          </div>
          <div className="col two">
            <h3>{report.site_hours.toFixed(2)} Hours</h3>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col three">
            <h3>Total Hours</h3>
          </div>
          <div className="col one">&nbsp;</div>
          <div className="col two">&nbsp;</div>
          <div className="col three">&nbsp;</div>
          <div className="col two">
            <h3>{totalHours(report).toFixed(2)} Hours</h3>
          </div>
        </div>
        <hr />
      </div>
      {report.hours_by_trail.length > 1 && (
        <div className="container">
          <h2>Hours by Trail Segment</h2>
          <p>
            Please enter <i>approximate</i> percentage of time used on each
            trail segment. The sum must total 100.
          </p>
          <hr />
          {report.hours_by_trail.map((seg_hours, index) => (
            <div className="row">
              <div className="col three">
                <h3>{seg_hours.trail_segment}</h3>
              </div>
              <div className="col three">
                <FormField
                  name={"seg_" + index}
                  type="text"
                  errors={errors}
                  className="wp_segment_pct"
                  value={seg_hours.wp_segment_pct}
                  onChange={handleTrailHoursChange}
                />
              </div>
            </div>
          ))}
          <hr />
        </div>
      )}
      <div className="container">
        {is_outreach ? (
          ""
        ) : (
          <div>
            <h2>Description</h2>

            <hr />
            <h3>Work Accomplished</h3>
            <div className="row">
              <div className="col three">
                <h3>New Trail?</h3>
              </div>
              <div className="col three">
                <FormField
                  name="newTrail"
                  type="checkbox"
                  checked={report.newTrail}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
              {report.newTrail ? (
                <div>
                  <div className="col three">
                    <h3>Tread built (ft)</h3>
                  </div>
                  <div className="col three">
                    <FormField
                      type="number"
                      name="treadBuilt"
                      step="0.1"
                      min="0"
                      value={report.treadBuilt}
                      errors={errors}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="col three">
                    <h3>Retread (ft)</h3>
                  </div>
                  <div className="col three">
                    <FormField
                      type="number"
                      name="retread"
                      step="0.1"
                      min="0"
                      value={report.retread}
                      errors={errors}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="row">
              <div className="col three">
                <h3>Miles covered</h3>
              </div>
              <div className="col three">
                <FormField
                  type="number"
                  name="milesCovered"
                  step="0.1"
                  min="0"
                  value={report.milesCovered}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
              <div className="col three">
                <h3>Rehab/reveg (ft)</h3>
              </div>
              <div className="col three">
                <FormField
                  type="number"
                  name="rehabRevegFt"
                  step="0.1"
                  min="0"
                  value={report.rehabRevegFt}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="row">
              <div className="col three">
                <h3>Logout (miles)</h3>
              </div>
              <div className="col three">
                <FormField
                  type="number"
                  name="logoutMiles"
                  step="0.1"
                  min="0"
                  value={report.logoutMiles}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
              <div className="col three">
                <h3>Logs removed (above 3")</h3>
              </div>
              <div className="col three">
                <FormField
                  type="number"
                  name="logsRemoved"
                  step="1"
                  min="0"
                  value={report.logsRemoved}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="row">
              <div className="col three">
                <h3>Brush cleared (miles)</h3>
              </div>
              <div className="col three">
                <FormField
                  type="number"
                  name="brushClearedMiles"
                  step="0.1"
                  min="0"
                  value={report.brushClearedMiles}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
              <div className="col three">
                <h3>Drains Cleaned or Created</h3>
              </div>
              <div className="col three">
                <FormField
                  type="number"
                  name="drains"
                  step="1"
                  min="0"
                  value={report.drains}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
            </div>
            <Structures
              name="structures"
              initialValue={report.structures}
              initialRemoved={report.structures_removed || []}
              errors={errors}
              onChange={handleChange}
              setRequestState={setRequestState}
            />
            <hr />
            <div className="row">
              <div className="col three">
                <h3>
                  Briefly provide a 2-3 sentence overview of work accomplished,
                  additional work completed beyond reveg, logs, drains or
                  structures, and any work remaining.
                </h3>
              </div>
              <div className="col seven">
                <FormField
                  type="textarea"
                  name="workRemaining"
                  value={report.workRemaining}
                  errors={errors}
                  onChange={handleChange}
                  style={{ height: "188px" }}
                  required
                />
              </div>
            </div>
            <hr />
          </div>
        )}

        {is_outreach ? (
          <div>
            <div className="row">
              <div className="col three">
                <h3>About how many people did you engage with?</h3>
              </div>
              <div className="col seven">
                <FormField
                  name="peopleEngaged"
                  type="select"
                  value={report.peopleEngaged}
                  errors={errors}
                  onChange={handleChange}
                >
                  <option value="">Please select one</option>
                  <option value="0-30">0-30</option>
                  <option value="30-60">30-60</option>
                  <option value="60-100">60-100</option>
                  <option value="&gt;100">&gt;100</option>
                </FormField>
                <br />
              </div>
            </div>
            <div className="row">
              <div className="col three">
                <h3>
                  Please let us know if there is any staff follow up needed,
                  such as passing on people/organizations you connected with or
                  questions you couldn’t answer.
                </h3>
              </div>
              <div className="col seven">
                <FormField
                  type="textarea"
                  style={{ height: "244px" }}
                  name="staffFollowUpNeeded"
                  value={report.staffFollowUpNeeded}
                  errors={errors}
                  onChange={handleChange}
                />
                <br />
              </div>
            </div>
            <div className="row">
              <div className="col three">
                <h3>Any highlights and/or challenges to share?</h3>
              </div>
              <div className="col seven">
                <FormField
                  type="textarea"
                  style={{ height: "244px" }}
                  name="ambassadorComments"
                  value={report.ambassadorComments}
                  errors={errors}
                  onChange={handleChange}
                />
                <br />
              </div>
            </div>
            <div className="row">
              <div className="col three">
                <h3>Do you think this event is worth doing again?</h3>
              </div>
              <div className="col seven">
                <FormField
                  name="eventWorthRepeating"
                  type="select"
                  value={report.eventWorthRepeating}
                  errors={errors}
                  onChange={handleChange}
                >
                  <option value="">Please select one</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </FormField>
                <br />
              </div>
            </div>
            <hr />
          </div>
        ) : (
          ""
        )}
        {report.oldStatus != "Published" ? (
          <div>
            <div className="row">
              <div className="col three">&nbsp;</div>
              <div className="col seven">
                <FormField
                  name="emailToCrew"
                  type="checkbox"
                  checked={report.emailToCrew}
                  errors={errors}
                  onChange={handleChange}
                  label="On 'Publish' email to crew members and post to Crew Corner"
                />
              </div>
            </div>
            <div className="row">
              <div className="col three">
                <h3>Message to crew</h3>
              </div>
              <div className="col seven">
                <FormField
                  name="messageToCrewTitle"
                  type="text"
                  value={report.messageToCrewTitle}
                  errors={errors}
                  onChange={handleChange}
                />
                <br />
                <FormField
                  type="textarea"
                  style={{ height: "244px" }}
                  name="messageToCrew"
                  value={report.messageToCrew}
                  errors={errors}
                  onChange={handleChange}
                />
                <br />
              </div>
            </div>
          </div>
        ) : (
          ""
        )}
        <div className="row">
          <div className="col three">
            <h3>Photos</h3>
          </div>
          <div className="col five">
            <FormField
              name="imageOrVideo"
              type="text"
              value={report.imageOrVideo}
              errors={errors}
              onChange={handleChange}
            />
            Enter the URL of a photo album to include with your report and
            message. If you have additional photo albums from other crew members
            you'd like to include, please enter those URLs in the message above.
            {report.imageOrVideo ? (
              <p>
                <img
                  src={report.imageOrVideo}
                  style={{ width: 200, height: "auto" }}
                />
              </p>
            ) : (
              ""
            )}
          </div>
        </div>
        <div className="row">
          <div className="col three">
            <h3>Photos Caption/Credit</h3>
          </div>
          <div className="col five">
            <FormField
              name="photoCaption"
              type="text"
              value={report.photoCaption}
              errors={errors}
              onChange={handleChange}
            />
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col three">
            <h3>Awards Owed on This Work Party</h3>
          </div>
          <div className="col five">
            <div>
              {report.awards_delivered && (
                <FormField
                  name="awardsDelivered"
                  type="checkboxes"
                  errors={errors}
                  onChange={handleAwardsDeliveredChange}
                  options={report.awards_delivered}
                  value={report.awards_value}
                />
              )}
            </div>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col three">
            <h3>Awards Owed for Future Work Parties</h3>
          </div>
          <div className="col five">
            <div>
              {report.awards_delivered && (
                <FormField
                  name="awardsFuture"
                  type="checkboxes"
                  errors={errors}
                  onChange={handleAwardsFutureChange}
                  options={report.awards_future}
                  value={report.awards_future_value}
                />
              )}
            </div>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col three">
            <h3>Status</h3>
          </div>
          <div className="col five">
            <div className="custom_select">
              <FormField
                name="status"
                type="select"
                value={report.status}
                errors={errors}
                onChange={handleChange}
              >
                <option>Draft</option>
                <option>Published</option>
              </FormField>
            </div>
          </div>
        </div>
        <hr />
        <button
          onClick={handleSubmit}
          className="ui button"
          disabled={submitting}
        >
          {submitting ? (
            "Submitting..."
          ) : (
            <span>
              <span className="icon-ok"></span> Save
            </span>
          )}
        </button>
        {error ? (
          <div className="validation-error">
            <div className="container">{error}</div>
          </div>
        ) : (
          ""
        )}
      </div>
    </form>
  );
}

function WPReportView({ wp, user, my_registration, initialValue }) {
  const [editing, setEditing] = React.useState(false);
  const [report, setReport] = React.useState(initialValue);

  function updateReport(value) {
    setReport(value);
    setEditing(false);
  }

  var reg = my_registration;
  var is_leader = utils.hasRole(user, reg, "CL");
  var additional_trails =
    report.additionalTrails && report.additionalTrails.split(";");
  var is_outreach = wp.work_party_type.name == "Outreach Event";

  var editReport =
    report.status == "Draft"
      ? Object.assign({}, report, { status: "Published" })
      : report;
  var center_style = { textAlign: "center" };
  if (is_leader && editing) {
    return (
      <WPReportForm
        wp={wp}
        initialValue={editReport}
        onSave={updateReport}
        action={wp.actions.savereport}
      />
    );
  }
  return (
    <div>
      <div className="container">
        <div className="module toolbar">
          <div>
            <h2>
              Status:{" "}
              <a href="#" style={{ cursor: "pointer" }}>
                {report.status}
              </a>
            </h2>
          </div>
          {report.status == "Draft" && is_leader ? (
            <div>
              <button
                onClick={() => setEditing(true)}
                className="ui button small"
              >
                <span className="icon-edit"></span> edit
              </button>
            </div>
          ) : (
            ""
          )}
        </div>
        <h2>Time & Location</h2>
        <hr />
        <div className="row">
          <div className="col three">
            <h3>Date</h3>
          </div>
          <div className="col seven">
            <p>
              <DateRange start={wp.start_date_time} end={wp.end_date_time} />
            </p>
          </div>
        </div>

        {is_outreach ? (
          ""
        ) : (
          <div>
            <hr />
            <div className="row">
              <div className="col three">
                <h3>Trails worked on</h3>
              </div>
              <div className="col seven">
                {wp.wp_trail_segments &&
                  wp.wp_trail_segments.map((wp_seg) => (
                    <p key={wp_seg.trail_segment.name}>
                      {wp_seg.trail_segment.name}
                    </p>
                  ))}
                {additional_trails &&
                  additional_trails.map((seg) => <p key={seg}>{seg}</p>)}
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col three">
                <h3>District</h3>
              </div>
              <div className="col seven">
                <p>{report.land_manager ? report.land_manager.name : ""}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <hr />
      <div className="container">
        <h2>Crew</h2>
        <hr />
        <div className="row">
          <div className="col three">
            <h3>Crew Leaders</h3>
          </div>
          <div className="col seven">
            <p>{wp.leader ? wp.leader.name : ""}</p>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col three">
            <h3>Participants</h3>
          </div>
          <div className="col seven">
            <p>
              {report.crew_count}{" "}
              {(is_outreach && "Ambassadors") || "Crew Members"} (
              {report.youth_count} Youth)
            </p>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col three">&nbsp;</div>
          <div className="col two">Hours</div>
          <div className="col one">&nbsp;</div>
          <div className="col two" style={center_style}>
            Participants
          </div>
          <div className="col one">&nbsp;</div>
          <div className="col two">Total</div>
        </div>

        {is_outreach ? (
          ""
        ) : (
          <div>
            <div className="row">
              <div className="col three">
                <h3>Site Preparation</h3>
              </div>
              <div className="col two">
                <h3>{report.sitePrepHours}</h3>
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-cancel"></span>
                </h3>
              </div>
              <div className="col two">
                <h3 style={center_style}>1 CL</h3>
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-eq"></span>
                </h3>
              </div>
              <div className="col two">
                <h3>{report.sitePrepHours} Hours</h3>
              </div>
            </div>
            <div className="row">
              <div className="col three">
                <h3>Administration</h3>
              </div>
              <div className="col two">
                <h3>{report.adminHours}</h3>
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-cancel"></span>
                </h3>
              </div>
              <div className="col two">
                <h3 style={center_style}>1 CL</h3>
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-eq"></span>
                </h3>
              </div>
              <div className="col two">
                <h3>{report.adminHours} Hours</h3>
              </div>
            </div>
            <div className="row">
              <div className="col three">
                <h3>Travel Time</h3>
              </div>
              <div className="col two">
                <h3>{report.travelHours}</h3>
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-cancel"></span>
                </h3>
              </div>
              <div className="col two">
                <h3 style={center_style}>{report.crew_count}</h3>
              </div>
              <div className="col one">
                <h3>
                  <span className="icon-eq"></span>
                </h3>
              </div>
              <div className="col two">
                <h3>{report.travelHours * report.crew_count} Hours</h3>
              </div>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col three">
            <h3>On Site Time</h3>
          </div>
          <div className="col two">
            <h3>{report.site_hours.toFixed(2)}</h3>
          </div>
          <div className="col one">&nbsp;</div>
          <div className="col two">&nbsp;</div>
          <div className="col one">
            <h3>
              <span className="icon-eq"></span>
            </h3>
          </div>
          <div className="col two">
            <h3>{report.site_hours.toFixed(2)} Hours</h3>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col three">
            <h3>Total Hours</h3>
          </div>
          <div className="col one">&nbsp;</div>
          <div className="col two">&nbsp;</div>
          <div className="col three">&nbsp;</div>
          <div className="col two">
            <h3>{totalHours(report).toFixed(2)} Hours</h3>
          </div>
        </div>
      </div>
      <hr />
      {report.hours_by_trail.length > 1 && (
        <div className="container">
          <h2>Hours by Trail Segment</h2>
          <hr />
          {report.hours_by_trail.map((seg_hours) => (
            <div className="row">
              <div className="col three">
                <h3>{seg_hours.trail_segment}</h3>
              </div>
              <div className="col three">
                <h3>
                  <progress value={seg_hours.wp_segment_pct} max="100" />
                </h3>
              </div>
              <div className="col two">
                <h3>{seg_hours.wp_segment_pct}%</h3>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="container">
        <h2>Description</h2>

        {is_outreach ? (
          ""
        ) : (
          <div>
            <hr />
            <div className="row">
              <div className="col three">
                <h3>Work Accomplished</h3>
              </div>
              <div className="col seven">
                <WorkAccomplishedSummary report={report} />
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col three">
                <h3>Work Remaining</h3>
              </div>
              <div className="col seven">
                <p>{report.workRemaining}</p>
              </div>
            </div>
          </div>
        )}
        <hr />
        {is_outreach ? (
          <div>
            <div className="row">
              <div className="col three">
                <h3>About how many people did you engage with?</h3>
              </div>
              <div className="col seven">
                <p>{report.peopleEngaged}</p>
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col three">
                <h3>
                  Please let us know if there is any staff follow up needed,
                  such as passing on people/organizations you connected with or
                  questions you couldn’t answer.
                </h3>
              </div>
              <div className="col seven">
                <p>{report.staffFollowUpNeeded}</p>
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col three">
                <h3>Any highlights and/or challenges to share?</h3>
              </div>
              <div className="col seven">
                <p>{report.ambassadorComments}</p>
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col three">
                <h3>Do you think this event is worth doing again?</h3>
              </div>
              <div className="col seven">
                <p>{report.eventWorthRepeating}</p>
              </div>
            </div>
            <hr />
          </div>
        ) : (
          ""
        )}
        <div className="row">
          <div className="col three">
            <h3>Message To Crew</h3>
          </div>
          <div className="col seven">
            <h3>{report.messageToCrewTitle}</h3>
            <p>{report.messageToCrew}</p>
          </div>
        </div>
        <hr />
        {report.awards_delivered && (
          <div className="row">
            <div className="col three">
              <h3>Awards Owed on This Work Party</h3>
            </div>
            <div className="col seven">
              {report.awards_delivered.map((award) => (
                <div key={award.value}>
                  {award.status == "Awarded" ? (
                    <span className="icon-ok"></span>
                  ) : (
                    <span className="icon-cancel"></span>
                  )}{" "}
                  {award.label}
                </div>
              ))}
            </div>
          </div>
        )}
        <hr />
        {report.awards_future && (
          <div className="row">
            <div className="col three">
              <h3>Awards Owed for Future Work Parties</h3>
            </div>
            <div className="col seven">
              {report.awards_future.map((award) => (
                <div key={award.value}>
                  {award.status == "Awarded" ? (
                    <span className="icon-ok"></span>
                  ) : (
                    <span className="icon-cancel"></span>
                  )}{" "}
                  {award.label}
                </div>
              ))}
            </div>
          </div>
        )}
        {report.imageOrVideo && (
          <div className="row">
            <div className="col three">
              <h3>Image</h3>
            </div>
            <div className="col five">
              <figure>
                <img
                  className="content"
                  src={report.imageOrVideo}
                  alt="Work Party Image"
                />
                {report.photoCaption && (
                  <figcaption>{report.photoCaption}</figcaption>
                )}
              </figure>
            </div>
          </div>
        )}
        <div className="module toolbar">
          <div>&nbsp;</div>
          {report.status == "Draft" && is_leader ? (
            <div>
              <button
                onClick={() => setEditing(true)}
                className="ui button small"
              >
                <span className="icon-edit"></span> edit
              </button>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
      <div className="container">
        <div className="module toolbar">
          <div></div>
          <div>
            <a
              href={wp.views.related_reports}
              className="ui button"
              style={{ marginTop: "-50px" }}
            >
              See All Summaries
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function WPReportLoader({ wp, user, my_registration }) {
  const data = useBackendData({
    url: wp.views.report,
  });

  return data ? (
    <WPReportView
      wp={wp}
      user={user}
      my_registration={my_registration}
      initialValue={data}
    />
  ) : (
    <div>Loading...</div>
  );
}

function WPReportsPagination(props) {
  function getDefaultProps() {
    return {
      num: 0,
      size: 10,
    };
  }

  props = {
    ...getDefaultProps(),
    ...props,
  };

  function handleClick(e) {
    e.preventDefault();
    var href = e.currentTarget.getAttribute("rel");
    var filters = utils.parseQueryString(new URL(href, location.href).search);
    props.updateQuery(filters);
  }

  var prev = props.prev_url ? (
    <li>
      <a href="#" rel={props.prev_url} onClick={handleClick}>
        Previous {props.size} items
      </a>
    </li>
  ) : (
    ""
  );
  var next = props.next_url ? (
    <li>
      <a href="#" rel={props.next_url} onClick={handleClick}>
        Next {props.size} items
      </a>
    </li>
  ) : (
    ""
  );
  var pages = props.urls.map(function (url, index) {
    return index == props.num ? (
      <li key={index}>
        <span className="pagenum">{index + 1}</span>
      </li>
    ) : (
      <li key={index}>
        <a href="#" rel={url} onClick={handleClick}>
          {index + 1}
        </a>
      </li>
    );
  }, null);
  return (
    <div className="ui">
      <ul className="pagination">
        {prev}
        {pages}
        {next}
      </ul>
    </div>
  );
}

function WPReportShortSummary({ wp, report }) {
  var is_outreach = wp.work_party_type.name == "Outreach Event";

  return (
    <div>
      {report.status == "Published" ? (
        <div className="item">
          <div className="meta">
            <h1>
              <a href={window.base_url + "workparty/" + wp._id}>{wp.name}</a>
            </h1>
          </div>
          <div className="meta">
            <h3>
              <span className="ico-svg">
                <img
                  src={window.base_url + "static/svg/calendar.svg"}
                  style={{ width: "20px" }}
                />
              </span>{" "}
              <DateRange start={wp.start_date_time} end={wp.end_date_time} />
            </h3>
          </div>

          {is_outreach ? (
            ""
          ) : (
            <div>
              <div className="meta">
                <h3>
                  Trails Worked On:
                  {wp.wp_trail_segments &&
                    wp.wp_trail_segments.map(function (wp_seg, i, arr) {
                      return (
                        <span key={wp_seg.trail_segment.name}>
                          {" "}
                          {wp_seg.trail_segment.name}
                          {i + 1 < arr.length && ","}
                        </span>
                      );
                    })}
                </h3>
              </div>
              <div className="meta">
                <h3>District: {wp.district} </h3>
              </div>
            </div>
          )}
          <div className="meta">
            <h3>Leader: {wp.leader && wp.leader.name}</h3>
            <h3>{report.crew_count} Participants</h3>
            <h3>
              {Math.round(report.totalHours).toLocaleString()} Total Hours
            </h3>
          </div>
          <br />

          <div className="container">
            {is_outreach ? (
              ""
            ) : (
              <div className="row">
                <div className="col">
                  <h3>Work Done</h3>
                  <WorkAccomplishedSummary report={report} />
                </div>
                <div className="col">
                  <h3>Work Remaining</h3>
                  <p>{report.workRemaining}</p>
                </div>
              </div>
            )}
            <hr />
            <br />
            <div className="row">
              <div className="col six">
                {report.imageOrVideo && (
                  <figure>
                    <img
                      className="content"
                      src={report.imageOrVideo}
                      alt="Work Party Image"
                    />
                    {report.photoCaption && (
                      <figcaption>{report.photoCaption}</figcaption>
                    )}
                  </figure>
                )}
              </div>
              <div className="col six">
                <h4>{report.messageToCrewTitle}</h4>
                <p>{report.messageToCrew}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </div>
  );
}

function WPReportRollup({ title, data }) {
  return data ? (
    <div className="module highlight">
      <div className="container" style={{ zIndex: 99999 }}>
        <h1>
          <span className="icon-chart-bar"></span>
          {title || "Trail Roll-Up"}
        </h1>
      </div>
      <div className="container">
        <h1 style={{ fontSize: "7em", lineHeight: "1em", margin: 0 }}>
          {Math.round(data.total_hours || 0).toLocaleString()}
        </h1>
        <h2>Total Hours</h2>
      </div>
      <hr />
      <div className="container">
        <h1 style={{ fontSize: "7em", lineHeight: "1em", margin: 0 }}>
          {data.crew_count || 0}
        </h1>
        <h2>Participants</h2>
      </div>
      <hr />
      <div className="container">
        <h1 style={{ fontSize: "7em", lineHeight: "1em", margin: 0 }}>
          {data.youth_count || 0}
        </h1>
        <h2>Youth</h2>
      </div>
    </div>
  ) : (
    <div>Loading ...</div>
  );
}

function WPReportListing({ data, updateQuery }) {
  var reports = data && data.reports;

  return reports ? (
    <section className="layout primary" style={{ marginTop: "22px" }}>
      <WPReportsPagination
        prev_url={reports.prev_url}
        next_url={reports.next_url}
        urls={reports.batch_urls}
        num={reports.num}
        updateQuery={updateQuery}
      />
      <div className="ReportSummaries">
        {reports && reports.items.length ? (
          reports.items.map((report) => (
            <WPReportShortSummary
              key={report.wp._id}
              report={report.report}
              wp={report.wp}
            />
          ))
        ) : (
          <span>No summaries found</span>
        )}
      </div>
      <WPReportsPagination
        prev_url={reports.prev_url}
        next_url={reports.next_url}
        urls={reports.batch_urls}
        num={reports.num}
        updateQuery={updateQuery}
      />
    </section>
  ) : (
    <section className="layout primary" style={{ marginTop: "22px" }}>
      <h2>No Results Found</h2>
    </section>
  );
}

var DATE_RANGES = [
  ["Last 30 Days", "Last 30 Days"],
  ["This Month", "This Month"],
  ["Last Month", "Last Month"],
  ["Last 365 Days", "Last 365 Days"],
  ["This Year", "This Year"],
  ["Last Year", "Last Year"],
  ["Custom", "Custom Dates"],
  ["All Time", "All Time"],
];

var WEEKDAY_OPTIONS = ["Weekends", "Weekdays"];

function ReportFilters({ filters, aggregations, updateQuery }) {
  const [state, setState] = React.useState(() => {
    var show_facets = ["days", "types", "regions", "features"];
    var show_form =
      show_facets.map((p) => filters[p] !== undefined).filter((bool) => bool)
        .length > 0;
    return {
      formCollapsed: !show_form,
    };
  });

  function toggleForm() {
    setState((currentState) => ({
      ...currentState,
      formCollapsed: !currentState.formCollapsed,
    }));
    return false;
  }

  return (
    <div className="module form subtle">
      <div className="ui panel" style={{ zIndex: 2 }}>
        <span
          className={
            "icon-plus-circled js-toggle" + (state.formCollapsed ? "" : " on")
          }
          onClick={toggleForm}
        ></span>
      </div>
      <div className="container" id="search_filters">
        <header>
          <h2>Filter Summaries</h2>
        </header>
        <form>
          <search.DateRangeFacet
            name="date"
            startName="start"
            endName="end"
            label="When"
            dateRanges={DATE_RANGES}
            value={filters}
            updateQuery={updateQuery}
          />
          <div className={"toggler" + (state.formCollapsed ? "" : " on")}>
            {aggregations ? (
              <div>
                <div className="day_filter">
                  <search.MultiChoiceFacet
                    name="days"
                    options={WEEKDAY_OPTIONS}
                    showAll={false}
                    value={filters.days}
                    updateQuery={updateQuery}
                  />
                </div>
                <hr />
                <search.MultiChoiceFacet
                  name="types"
                  label="Work Party Types"
                  options={aggregations.types.buckets.map((t) => ({
                    value: t.key,
                    label: t.key,
                    count: t.doc_count,
                  }))}
                  showAll={true}
                  value={filters.types}
                  updateQuery={updateQuery}
                />
                <hr />
                <search.MultiChoiceFacet
                  name="regions"
                  label="Regions"
                  options={aggregations.regions.buckets.map((t) => ({
                    value: t.key,
                    label: t.key,
                    count: t.doc_count,
                  }))}
                  showAll={true}
                  value={filters.regions}
                  updateQuery={updateQuery}
                />
                <hr />
                <search.MultiChoiceFacet
                  name="features"
                  label="Features"
                  options={aggregations.features.buckets.map((t) => ({
                    value: t.key,
                    label: t.key,
                    count: t.doc_count,
                  }))}
                  showAll={true}
                  value={filters.features}
                  updateQuery={updateQuery}
                />
                <hr />
              </div>
            ) : (
              ""
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function WPReportSearch({ url: baseUrl, title, rollupTitle, searchAllUrl }) {
  const initialUrl = baseUrl + location.search;
  const qs = location.search.substring(1);
  const [filters, setFilters] = React.useState(utils.parseQueryString(qs));
  const [url, setUrl] = React.useState(initialUrl);
  const data = useBackendData({ url, resetWhileLoading: false });

  const handlePopState = React.useEffectEvent(onPopState);
  React.useEffect(() => {
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  function searchUpdate(event) {
    event.preventDefault();
    var search = document.querySelector('input[name="text"]');
    if (search) {
      updateQuery({ text: search.value });
    }
  }

  function updateQuery(changedFilters) {
    var url = baseUrl,
      viewUrl = window.base_url + "search-reports",
      loc;
    var newFilters = { ...filters };
    if (changedFilters.batch_num == undefined) {
      delete filters.batch_num;
    }
    Object.keys(changedFilters).forEach(function (k) {
      var v = changedFilters[k];
      if (v === undefined) {
        delete newFilters[k];
      } else {
        newFilters[k] = v;
      }
    });
    if (newFilters) {
      var query = [];
      Object.keys(newFilters).forEach(function (k) {
        var v = newFilters[k];
        if (Array.isArray(v)) {
          v.forEach(function (v) {
            query.push(k + "=" + encodeURIComponent(v));
          });
        } else {
          query.push(k + "=" + encodeURIComponent(v));
        }
      });
      loc = "?" + query.join("&");
      url += loc;
      viewUrl += loc;
    }
    setFilters(newFilters);
    setUrl(url);

    history.pushState(newFilters, "Report Search", viewUrl);
  }

  function onPopState() {
    var qs = window.location.search.substring(1);
    const url = baseUrl + location.search;
    if (url != window.location.href) {
      setFilters(utils.parseQueryString(qs));
      setUrl(url);
    }
  }

  const aggregations = data ? data.aggregations : null;
  return (
    <article className-="wrapper">
      <header role="heading" className="layout">
        <h1>{title || "Work Party Summaries"}</h1>
        {searchAllUrl && data && data.trails ? (
          <h2>
            Search summaries for trails: {data.trails.join(", ")} (
            <a href={searchAllUrl} title="Search in all Work Party Summaries">
              search all
            </a>
            )
          </h2>
        ) : (
          <h2>Search</h2>
        )}
        <form>
          <div className="row">
            <div className="col ten">
              <search.TextFacet
                name="text"
                placeholder="keywords"
                initialValue={filters ? filters.text : ""}
                updateQuery={updateQuery}
              />
            </div>
            <div className="col two">
              <a href="" className="ui button small" onClick={searchUpdate}>
                Search
              </a>
            </div>
          </div>
        </form>
      </header>
      <section className="layout secondary right">
        <ReportFilters
          aggregations={aggregations}
          filters={filters}
          updateQuery={updateQuery}
        />
        <WPReportRollup title={rollupTitle || "Report Roll-Up"} data={data} />
      </section>
      {data ? (
        <WPReportListing data={data} updateQuery={updateQuery} />
      ) : (
        <div>Loading ...</div>
      )}
    </article>
  );
}

export { ReportFilters, WPReportLoader, WPReportSearch };
